import { useCallback, useEffect, useRef } from 'react';
import { analyticsService, EventData, EventType, createEventData } from '../services/analyticsApi';

// Event queue for batching
interface QueuedEvent {
  eventData: EventData;
  timestamp: number;
}

class AnalyticsQueue {
  private queue: QueuedEvent[] = [];
  private batchSize = 5;
  private maxWaitTime = 10000; // 10 seconds
  private timer: NodeJS.Timeout | null = null;

  constructor() {
    // Send any remaining events when page is about to unload
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => {
        this.flush();
      });

      // Also flush periodically
      setInterval(() => {
        if (this.queue.length > 0) {
          this.flush();
        }
      }, 30000); // Every 30 seconds
    }
  }

  add(eventData: EventData) {
    this.queue.push({
      eventData,
      timestamp: Date.now(),
    });

    // If we have enough events or timer is not running, process queue
    if (this.queue.length >= this.batchSize) {
      this.flush();
    } else if (!this.timer) {
      this.timer = setTimeout(() => {
        this.flush();
      }, this.maxWaitTime);
    }
  }

  private flush() {
    if (this.queue.length === 0) return;

    const events = this.queue.map(item => item.eventData);
    this.queue = [];

    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }

    // Send events to API
    analyticsService.trackEvents(events);
  }
}

// Global queue instance
const globalQueue = new AnalyticsQueue();

// Session management for deduplication
class SessionManager {
  private sessionKey = 'analytics_session';
  private sessionDuration = 30 * 60 * 1000; // 30 minutes

  getSessionId(): string {
    const stored = localStorage.getItem(this.sessionKey);
    
    if (stored) {
      const { sessionId, timestamp } = JSON.parse(stored);
      if (Date.now() - timestamp < this.sessionDuration) {
        return sessionId;
      }
    }

    // Create new session
    const newSessionId = this.generateSessionId();
    localStorage.setItem(this.sessionKey, JSON.stringify({
      sessionId: newSessionId,
      timestamp: Date.now(),
    }));
    
    return newSessionId;
  }

  private generateSessionId(): string {
    return Math.random().toString(36).substring(2, 15) + 
           Math.random().toString(36).substring(2, 15);
  }

  extendSession() {
    const stored = localStorage.getItem(this.sessionKey);
    if (stored) {
      const { sessionId } = JSON.parse(stored);
      localStorage.setItem(this.sessionKey, JSON.stringify({
        sessionId,
        timestamp: Date.now(),
      }));
    }
  }
}

const sessionManager = new SessionManager();

// Rate limiting per event type
class RateLimiter {
  private limits: Map<string, number> = new Map();
  private windowSize = 300000; // 5 minutes (for WhatsApp clicks)

  canTrack(eventKey: string, limit: number = 1): boolean {
    const now = Date.now();
    const key = `${eventKey}_${Math.floor(now / this.windowSize)}`;
    
    const currentCount = this.limits.get(key) || 0;
    if (currentCount >= limit) {
      return false;
    }

    this.limits.set(key, currentCount + 1);
    
    // Cleanup old entries
    const keysToDelete: string[] = [];
    this.limits.forEach((_, k) => {
      const keyTime = parseInt(k.split('_').pop() || '0');
      if (now - (keyTime * this.windowSize) > this.windowSize * 2) {
        keysToDelete.push(k);
      }
    });
    keysToDelete.forEach(k => this.limits.delete(k));

    return true;
  }
}

const rateLimiter = new RateLimiter();

// Main analytics hook
export const useAnalytics = () => {
  const trackEvent = useCallback((
    type: EventType,
    additionalData: Partial<EventData> = {}
  ) => {
    // Rate limiting
    const eventKey = `${type}_${additionalData.car_id || 'global'}`;
    if (!rateLimiter.canTrack(eventKey)) {
      return;
    }

    // Create event data
    const eventData = createEventData(type, additionalData);
    
    // Add to queue
    globalQueue.add(eventData);
    
    // Extend session
    sessionManager.extendSession();
  }, []);

  return { trackEvent };
};

// Car view tracking hook
export const useCarViewTracking = (carId: number) => {
  const { trackEvent } = useAnalytics();
  const startTimeRef = useRef<number>(Date.now());
  const isTrackingRef = useRef<boolean>(false);
  const hasTrackedRef = useRef<boolean>(false);

  useEffect(() => {
    startTimeRef.current = Date.now();
    isTrackingRef.current = true;
    hasTrackedRef.current = false;

    // Track view after minimum duration
    const minViewTimer = setTimeout(() => {
      if (isTrackingRef.current && !hasTrackedRef.current) {
        const duration = Math.floor((Date.now() - startTimeRef.current) / 1000);
        if (duration >= 10) { // Minimum 10 seconds
          trackEvent('car_view', {
            car_id: carId,
            view_duration: duration,
          });
          hasTrackedRef.current = true;
        }
      }
    }, 10000); // Wait 10 seconds

    return () => {
      clearTimeout(minViewTimer);
      
      // Track final duration if user leaves
      if (isTrackingRef.current && !hasTrackedRef.current) {
        const duration = Math.floor((Date.now() - startTimeRef.current) / 1000);
        if (duration >= 10) {
          trackEvent('car_view', {
            car_id: carId,
            view_duration: duration,
          });
        }
      }
      
      isTrackingRef.current = false;
    };
  }, [carId, trackEvent]);

  return {
    getCurrentDuration: useCallback(() => {
      return Math.floor((Date.now() - startTimeRef.current) / 1000);
    }, []),
  };
};

// WhatsApp click tracking hook
export const useWhatsAppTracking = () => {
  const { trackEvent } = useAnalytics();
  
  // Separate rate limiter for WhatsApp with 5 minute window
  const whatsAppRateLimiter = useRef(new class {
    private clicks: Map<string, number> = new Map();
    
    canClick(carId: number): boolean {
      const now = Date.now();
      const windowSize = 300000; // 5 minutes
      const key = `whatsapp_${carId}_${Math.floor(now / windowSize)}`;
      
      const lastClick = this.clicks.get(key) || 0;
      if (lastClick > 0) {
        return false; // Already clicked in this 5-minute window
      }
      
      this.clicks.set(key, now);
      
      // Cleanup old entries
      const keysToDelete: string[] = [];
      this.clicks.forEach((_, k) => {
        const keyTime = parseInt(k.split('_').pop() || '0');
        if (now - (keyTime * windowSize) > windowSize * 2) {
          keysToDelete.push(k);
        }
      });
      keysToDelete.forEach(k => this.clicks.delete(k));
      
      return true;
    }
  }()).current;

  const trackWhatsAppClick = useCallback((
    carId: number,
    language: 'tr' | 'ar' | 'es' | 'uk' | 'ru',
    phoneNumber?: string
  ) => {
    // Check WhatsApp-specific rate limit
    if (!whatsAppRateLimiter.canClick(carId)) {
      // WhatsApp click rate limited
      return;
    }
    
    trackEvent('whatsapp_click', {
      car_id: carId,
      language,
      phone_number: phoneNumber,
    });
  }, [trackEvent, whatsAppRateLimiter]);

  return { trackWhatsAppClick };
};

// Search tracking hook
export const useSearchTracking = () => {
  const { trackEvent } = useAnalytics();

  const trackSearch = useCallback((
    searchFilters: any,
    resultCount: number,
    selectedCarId?: number
  ) => {
    trackEvent('search', {
      search_filters: searchFilters,
      result_count: resultCount,
      selected_car_id: selectedCarId,
    });
  }, [trackEvent]);

  return { trackSearch };
};

// Page view tracking hook with automatic cleanup
export const usePageViewTracking = (pageName: string) => {
  const startTimeRef = useRef<number>(Date.now());

  useEffect(() => {
    startTimeRef.current = Date.now();

    // Optional: Track page entry
    // Page view started

    return () => {
      const duration = Date.now() - startTimeRef.current;
      // Page view ended
      
      // Could track page exit if needed for bounce rate calculation
    };
  }, [pageName]);

  return {
    getPageDuration: useCallback(() => {
      return Date.now() - startTimeRef.current;
    }, []),
  };
};

// Performance tracking hook
export const usePerformanceTracking = () => {
  const trackLoadTime = useCallback((pageName: string, loadTime: number) => {
    // Could extend to track performance metrics
    // Page load time tracked
  }, []);

  return { trackLoadTime };
};

// Local storage helper for offline events
class OfflineEventStorage {
  private storageKey = 'analytics_offline_events';
  private maxEvents = 100;

  storeEvent(eventData: EventData) {
    try {
      const stored = this.getStoredEvents();
      stored.push(eventData);
      
      // Keep only the latest events
      if (stored.length > this.maxEvents) {
        stored.splice(0, stored.length - this.maxEvents);
      }
      
      localStorage.setItem(this.storageKey, JSON.stringify(stored));
    } catch (error) {
      // Failed to store offline event
    }
  }

  getStoredEvents(): EventData[] {
    try {
      const stored = localStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      // Failed to retrieve offline events
      return [];
    }
  }

  clearStoredEvents() {
    localStorage.removeItem(this.storageKey);
  }

  async syncOfflineEvents() {
    const events = this.getStoredEvents();
    if (events.length > 0) {
      try {
        await analyticsService.trackEvents(events);
        this.clearStoredEvents();
        // Offline events synced successfully
      } catch (error) {
        // Failed to sync offline events
      }
    }
  }
}

// Export for use in components that need offline capability
export const offlineEventStorage = new OfflineEventStorage();

// Hook for managing online/offline state
export const useOfflineEventSync = () => {
  useEffect(() => {
    const handleOnline = () => {
      offlineEventStorage.syncOfflineEvents();
    };

    window.addEventListener('online', handleOnline);
    
    // Also sync when component mounts if online
    if (navigator.onLine) {
      handleOnline();
    }

    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, []);
};